import { api, formatTenne } from "@awtoban/shared";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@awtoban/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import ReactECharts from "echarts-for-react";
import { LogOut } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "../../i18n";
import { LocaleSwitcher } from "../../i18n/LocaleSwitcher";
import { useDebouncedValue } from "../../lib/useDebouncedValue";
import { useAuth } from "../auth/AuthProvider";

interface StatsOverview {
  today: {
    tx_count: number;
    total_revenue_tenne: number;
    avg_total_tenne: number;
    oversized_pct: number;
  };
  all_time: { tx_count: number };
  per_station_today: {
    station_code: string;
    station_name: string;
    tx_count: number;
    revenue_tenne: number;
  }[];
}

interface CityNames {
  name_tk: string;
  name_ru: string;
  name_en: string;
}

interface TxRow {
  id: string;
  invoice_number: string;
  status: string;
  created_at: string;
  vehicle_plate: string;
  total_tenne: number;
  station: number;
  from_city: CityNames | null;
  to_city: CityNames | null;
}

function bcp47(locale: Locale): string {
  if (locale === "en") return "en-US";
  if (locale === "ru") return "ru-RU";
  return "tk-TM";
}

function cityName(c: CityNames | null, locale: Locale): string {
  if (!c) return "—";
  if (locale === "ru") return c.name_ru || c.name_tk;
  if (locale === "en") return c.name_en || c.name_tk;
  return c.name_tk;
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const money = bcp47(locale);
  const auth = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState("");
  const [voidTarget, setVoidTarget] = React.useState<TxRow | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);

  const statsQuery = useQuery({
    queryKey: ["hq-stats"],
    refetchInterval: 15_000,
    queryFn: async () => {
      const { data } = await api.GET("/api/v1/hq/stats/overview", {});
      return data as unknown as StatsOverview;
    },
  });

  const txQuery = useQuery({
    queryKey: ["hq-tx", debouncedSearch],
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } = await api.GET("/api/v1/hq/transactions/", {
        params: { query: debouncedSearch ? { q: debouncedSearch } : {} },
      } as never);
      return data as unknown as { count: number; results: TxRow[] };
    },
  });

  const tsQuery = useQuery({
    queryKey: ["hq-timeseries"],
    refetchInterval: 15_000,
    queryFn: async () => {
      const { data } = await api.GET("/api/v1/hq/stats/timeseries", {});
      return data as unknown as {
        date: string;
        series: { hour: number; tx_count: number; revenue_tenne: number }[];
      };
    },
  });

  const stats = statsQuery.data;

  return (
    <div className="min-h-screen bg-paper-soft">
      <header className="flex flex-wrap items-center gap-x-6 gap-y-2 border-line-strong border-b bg-paper px-4 py-3 sm:px-6">
        <div>
          <div className="text-[11px] text-ink-muted uppercase tracking-wider">
            {t("app.dashboard")}
          </div>
          <div className="font-semibold">
            {t("app.title")} — {t("app.subtitle")}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-4 text-sm sm:gap-6">
          <Link to="/operators" className="text-ink-muted hover:text-ink">
            {t("nav.operators")}
          </Link>
          <Link to="/companies" className="text-ink-muted hover:text-ink">
            {t("nav.companies")}
          </Link>
          <Link to="/integrity" className="text-ink-muted hover:text-ink">
            {t("nav.integrity")}
          </Link>
          <Link to="/pricing/compare" className="text-ink-muted hover:text-ink">
            {t("nav.pricing")}
          </Link>
          <div>
            <div className="text-[11px] text-ink-muted uppercase tracking-wider">
              {t("app.user")}
            </div>
            <div className="font-medium">{auth.user?.username}</div>
          </div>
          <LocaleSwitcher />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              auth.logout();
              void navigate({ to: "/login" });
            }}
          >
            <LogOut className="h-4 w-4" />
            {t("auth.log_out")}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] space-y-6 px-4 py-4 sm:px-6 sm:py-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiCard
            label={t("kpi.today_revenue")}
            value={stats ? `${formatTenne(stats.today.total_revenue_tenne, money)} TMT` : "—"}
            sublabel={stats ? t("kpi.transactions_count", { count: stats.today.tx_count }) : ""}
          />
          <KpiCard
            label={t("kpi.today_transactions")}
            value={stats ? String(stats.today.tx_count) : "—"}
            sublabel={
              stats
                ? t("kpi.avg_per_tx", { amount: formatTenne(stats.today.avg_total_tenne, money) })
                : ""
            }
          />
          <KpiCard
            label={t("kpi.today_oversized")}
            value={stats ? `${stats.today.oversized_pct}%` : "—"}
            sublabel={t("kpi.of_today_traffic")}
          />
          <KpiCard
            label={t("kpi.total_transactions")}
            value={stats ? String(stats.all_time.tx_count) : "—"}
            sublabel={t("kpi.since_golive")}
          />
        </div>

        <section className="rounded-md border border-line-strong bg-paper p-4">
          <h2 className="mb-2 font-medium text-ink-muted text-xs uppercase tracking-wider">
            {t("chart.revenue_per_hour")}
          </h2>
          {tsQuery.data ? (
            <ReactECharts
              style={{ height: 240 }}
              option={{
                grid: { left: 60, right: 16, top: 16, bottom: 36 },
                tooltip: {
                  trigger: "axis",
                  formatter: (params: { axisValue: string; value: number }[]) => {
                    const p = params[0];
                    if (!p) return "";
                    return `${p.axisValue}:00 — ${formatTenne(p.value, money)} TMT`;
                  },
                },
                xAxis: {
                  type: "category",
                  data: tsQuery.data.series.map((p) => String(p.hour).padStart(2, "0")),
                  axisLine: { lineStyle: { color: "#bbb" } },
                  axisTick: { show: false },
                },
                yAxis: {
                  type: "value",
                  axisLabel: { formatter: (v: number) => formatTenne(v, money) },
                  splitLine: { lineStyle: { color: "#eee" } },
                },
                series: [
                  {
                    name: t("chart.revenue"),
                    type: "bar",
                    data: tsQuery.data.series.map((p) => p.revenue_tenne),
                    itemStyle: { color: "#0c7a4d" },
                    barWidth: "70%",
                  },
                ],
              }}
            />
          ) : (
            <div className="h-[240px] animate-pulse rounded bg-paper-soft" />
          )}
        </section>

        <section className="rounded-md border border-line-strong bg-paper">
          <h2 className="border-line border-b px-4 py-2 font-medium text-ink-muted text-xs uppercase tracking-wider">
            {t("stations.title")}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] table-fixed text-[14px]">
              <colgroup>
                <col className="w-24" />
                <col />
                <col className="w-32" />
                <col className="w-40" />
              </colgroup>
              <thead>
                <tr className="border-line border-b text-ink-muted text-xs uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">{t("stations.code")}</th>
                  <th className="px-4 py-2 text-left">{t("stations.station")}</th>
                  <th className="px-4 py-2 text-right">{t("stations.transactions")}</th>
                  <th className="px-4 py-2 text-right">{t("stations.revenue")}</th>
                </tr>
              </thead>
              <tbody>
                {statsQuery.isLoading ? (
                  <SkeletonRows cols={4} rows={3} />
                ) : stats?.per_station_today.length ? (
                  stats.per_station_today.map((s) => (
                    <tr key={s.station_code} className="border-line border-b last:border-b-0">
                      <td className="px-4 py-2 font-medium">{s.station_code}</td>
                      <td className="truncate px-4 py-2" title={s.station_name}>
                        {s.station_name}
                      </td>
                      <td className="tabular px-4 py-2 text-right">{s.tx_count}</td>
                      <td className="tabular px-4 py-2 text-right">
                        {formatTenne(s.revenue_tenne, money)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-ink-muted text-sm">
                      {t("stations.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-md border border-line-strong bg-paper">
          <header className="flex flex-wrap items-center justify-between gap-3 border-line border-b px-4 py-2">
            <h2 className="font-medium text-ink-muted text-xs uppercase tracking-wider">
              {t("tx.title")}
            </h2>
            <input
              type="search"
              aria-label={t("tx.search_placeholder")}
              placeholder={t("tx.search_placeholder")}
              className="h-8 w-full rounded-sm border border-line-strong bg-paper px-2 text-sm focus:border-ink focus:outline-none sm:w-64"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </header>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] table-fixed text-[13px]">
              <colgroup>
                <col className="w-28" />
                <col className="w-40" />
                <col className="w-28" />
                <col />
                <col className="w-32" />
                <col className="w-28" />
              </colgroup>
              <thead>
                <tr className="border-line border-b text-[11px] text-ink-muted uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">{t("tx.invoice")}</th>
                  <th className="px-3 py-2 text-left">{t("tx.when")}</th>
                  <th className="px-3 py-2 text-left">{t("tx.plate")}</th>
                  <th className="px-3 py-2 text-left">{t("tx.route")}</th>
                  <th className="px-3 py-2 text-right">{t("tx.total")}</th>
                  <th className="px-3 py-2 text-left">{t("tx.status")}</th>
                </tr>
              </thead>
              <tbody>
                {txQuery.isLoading ? (
                  <SkeletonRows cols={6} rows={5} />
                ) : txQuery.data?.results.length ? (
                  txQuery.data.results.map((tx) => {
                    const route = `${cityName(tx.from_city, locale)} → ${cityName(tx.to_city, locale)}`;
                    return (
                      <tr
                        key={tx.id}
                        className="border-line border-b last:border-b-0 hover:bg-paper-soft"
                      >
                        <td className="tabular px-3 py-2 font-medium">{tx.invoice_number}</td>
                        <td className="tabular px-3 py-2 text-ink-muted">
                          {new Date(tx.created_at).toLocaleString(money, {
                            hour12: false,
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="tabular px-3 py-2">{tx.vehicle_plate}</td>
                        <td className="truncate px-3 py-2 text-ink-muted" title={route}>
                          {route}
                        </td>
                        <td className="tabular px-3 py-2 text-right font-medium">
                          {formatTenne(tx.total_tenne, money)} TMT
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={tx.status} />
                            {tx.status !== "void" ? (
                              <button
                                type="button"
                                onClick={() => setVoidTarget(tx)}
                                className="text-[11px] text-ink-subtle underline decoration-dotted hover:text-danger"
                              >
                                {t("tx.void")}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-ink-muted text-sm">
                      {t("tx.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <VoidDialog
        key={voidTarget?.id ?? "none"}
        tx={voidTarget}
        money={money}
        onClose={() => setVoidTarget(null)}
        onVoided={() => {
          setVoidTarget(null);
          void txQuery.refetch();
          void statsQuery.refetch();
        }}
      />
    </div>
  );
}

function VoidDialog({
  tx,
  money,
  onClose,
  onVoided,
}: {
  tx: TxRow | null;
  money: string;
  onClose: () => void;
  onVoided: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  if (!tx) return null;

  const confirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    const { error } = await api.POST("/api/v1/hq/transactions/{tx_uuid}/void", {
      params: { path: { tx_uuid: tx.id } },
      body: { reason } as never,
    });
    setSubmitting(false);
    if (error) {
      toast.error(t("void.error"));
      return;
    }
    toast.success(t("void.voided", { number: tx.invoice_number }));
    onVoided();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("void.title")}</DialogTitle>
          <DialogDescription>
            {t("void.body", {
              number: tx.invoice_number,
              amount: `${formatTenne(tx.total_tenne, money)} TMT`,
            })}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="void-reason">{t("void.reason")}</Label>
          <Input
            id="void-reason"
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            {t("void.cancel")}
          </Button>
          <Button variant="danger" disabled={submitting} onClick={() => void confirm()}>
            {submitting ? t("void.voiding") : t("void.confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KpiCard({ label, value, sublabel }: { label: string; value: string; sublabel?: string }) {
  return (
    <div className="rounded-md border border-line-strong bg-paper p-4">
      <div className="text-[11px] text-ink-muted uppercase tracking-wider">{label}</div>
      <div className="tabular mt-1 font-semibold text-2xl">{value}</div>
      {sublabel ? <div className="mt-1 text-[12px] text-ink-muted">{sublabel}</div> : null}
    </div>
  );
}

function SkeletonRows({ cols, rows }: { cols: number; rows: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => r).map((r) => (
        <tr key={r} className="border-line border-b last:border-b-0">
          {Array.from({ length: cols }, (_, c) => c).map((c) => (
            <td key={c} className="px-3 py-2.5">
              <div className="h-3 w-full animate-pulse rounded bg-paper-soft" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const styles: Record<string, string> = {
    issued: "bg-paper-soft text-ink border-line-strong",
    paid: "bg-success/10 text-success-fg border-success/30",
    void: "bg-danger/10 text-danger-fg border-danger/30",
  };
  const cls = styles[status] ?? styles.issued;
  return (
    <span
      className={`inline-block rounded-sm border px-2 py-0.5 text-[11px] uppercase tracking-wider ${cls}`}
    >
      {t(`status.${status}`)}
    </span>
  );
}
